<template>
  <div class="relative">
    <!-- Toggle -->
    <div class="relative mt-12 flex justify-center sm:mt-16 px-6">
      <div class="flex rounded-lg bg-gray-100 p-0.5">
        <button type="button"
          class="relative rounded-md  py-2 px-6 text-sm font-medium shadow-sm focus:z-10 focus:outline-none"
          :class="paymentFrequency !== 'monthly' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
          @click="paymentFrequency = 'monthly'">
          {{ $t('pricing.monthlyBilling').toString() }}
        </button>
        <button type="button"
          class="relative ml-0.5 rounded-md border border-transparent py-2 px-6 text-sm font-medium focus:z-10 focus:outline-none"
          :class="paymentFrequency !== 'annually' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
          @click="paymentFrequency = 'annually'">
          {{ $t('pricing.annualBilling').toString() }}
        </button>
      </div>
    </div>

    <!-- Cards -->
    <div class="relative mx-auto mt-8 max-w-2xl px-6 pb-8 sm:mt-12 xl:max-w-7xl xl:px-8 xl:pb-0">
      <div class="relative space-y-6 xl:grid xl:grid-cols-4 xl:space-y-0 xl:bg-backgroundgray rounded-tl-lg rounded-tr-lg">
        <div v-for="price of pricing" :key="price.key" :class="{'bg-backgroundgray xl:bg-transparent': true}"
          class="py-6 px-6 rounded-lg xl:px-8">
          <div>
            <h3 :class="{'text-standard': true}" class="uppercase font-semibold">{{
              $t(`pricing.tiers.${price.key}.name`) }}</h3>
            <div class="flex flex-col items-start sm:flex-row sm:items-center sm:justify-between xl:flex-col xl:items-start">
              <div class="mt-3 flex items-center">
                <p :class="{'text-standard': true}" class="text-4xl font-bold tracking-tight">
                  <img :src="price.icon" class="h-full" style="max-width: 50px;height:50px"></p>
                <div class="ml-4 w-full">
                  <p :class="{'text-standard': true}" class="text-sm"
                    v-html="$t(`pricing.tiers.${price.key}.description`).toString()" />
                </div>
              </div>
              <div :class="{'text-standard': true}" v-if="typeof price.monthlyPrice === 'number'">
                <p class="">
                  €<span class="text-4xl font-medium sm:mt-4">{{ $n(monthlyPayment ? price.monthlyPrice :
                    price.annualPrice/12, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span>
                  /{{ $t(`pricing.monthly`) }} <span v-if="!monthlyPayment">**</span>
                </p>
                <p v-if="!monthlyPayment" class="text-lightgray text-xs">** {{ $t(`pricing.payedAnnually`) }} €<b>{{
                    $n(price.annualPrice, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</b></p>
              </div>
              <div v-else :class="{'text-standard': true}">
                <p class="text-4xl">
                  <span class="font-medium sm:mt-4 text-3xl">{{ $t(monthlyPayment ? price.monthlyPrice : price.annualPrice) }}</span>
                </p>
                <p v-if="!monthlyPayment" class="text-lightgray text-xs">&nbsp;</p>
              </div>
            </div>
            <div v-if="!hideBuyButtons" class="mb-8">
              <a :href="typeof price.monthlyPrice === 'number' ? panelLinks.registerUrl.value : contactUrl"
                class="bg-highlight text-white mt-6 w-full inline-block py-2 px-8 border border-transparent rounded-md shadow-sm text-center font-medium lg:mt-6 lg:w-full">
                <template v-if="typeof price.monthlyPrice === 'number'">
                  {{ $t("pricing.buy") }} {{ $t(`pricing.tiers.${price.key}.name`) }}
                </template>
                <template v-else>
                  {{ $t("pricing.contactUs") }}
                </template>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Feature comparison (up to xl) -->
    <section aria-labelledby="mobile-comparison-heading" class="xl:hidden">
      <h2 id="mobile-comparison-heading" class="sr-only">Feature comparison</h2>
      <div class="mx-auto max-w-2xl px-6">
        <div class="w-full grid grid-cols-4 justify-between text-sm">
          <div v-for="price of pricing" :key="price.key"
            :class="{'bg-highlight text-white': price.key === selectedPlan, 'bg-white': price.key !== selectedPlan}"
            class="text-center py-4 px-2" @click="selectedPlan = price.key">
            {{ price.shortname }}
          </div>
        </div>
      </div>

      <div class="mx-auto max-w-2xl space-y-16 pb-16 px-6">
        <div v-for="price of filteredPlans" :key="price.key"
          :class="{'border-highlight': false, 'border-gray-200': true}" class="border-t">
          <div class="border-transparent -mt-px pt-6 border-t-2 sm:w-1/2">
            <h3 class="text-gray-900 text-sm font-bold uppercase">{{ $t(`pricing.tiers.${price.key}.name`) }}</h3>
            <div class="mt-3 flex items-center">
              <p class="text-4xl font-bold tracking-tight w-1/3"><img :src="price.icon" class="h-full"
                  style="max-width: 50px"></p>
              <div class="ml-4 w-full">
                <p class="text-sm" v-html="$t(`pricing.tiers.${price.key}.description`).toString()" />
              </div>
            </div>
          </div>
          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.featuresTitle') }}</h4>

          <div class="relative mt-6">
            <PricingTableBlock :keys="featuresKeys" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>

          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.uploadDistributionTitle') }}</h4>
          <div class="relative mt-6">
            <PricingTableBlock :keys="distributionKeys" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>

          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.utilitiesTitle') }}</h4>
          <div class="relative mt-6">
            <PricingTableBlock :keys="utilitiesKeys" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>

          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.teamsTitle') }}</h4>
          <div class="relative mt-6">
            <PricingTableBlock :keys="teamKeys" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>

          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.supportTitle') }}</h4>
          <div class="relative mt-6">
            <PricingTableBlock :keys="supportKeys" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>

          <h4 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.3dSupportTitle') }}</h4>
          <div class="relative mt-6">
            <PricingTableBlock :keys="modellingSupport" :payment-frequency="paymentFrequency" mobile
              :selected-plan="selectedPlan" />
          </div>
          <div class="flex w-1/2">
            <p class="text-xs text-gray-400 px-4 mt-4">{{ $t('pricing.annualOnly') }}</p>
          </div>

        </div>
      </div>
    </section>

    <!-- Toggle (bottom) (up to xl) -->
    <div class="relative mt-12 flex justify-center sm:mt-16 px-6 xl:hidden">
      <div class="flex rounded-lg bg-gray-100 p-0.5">
        <button type="button"
          :class="paymentFrequency !== 'monthly' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
          class="relative rounded-md py-2 px-6 text-sm font-medium shadow-sm focus:z-10 focus:outline-none"
          @click="paymentFrequency = 'monthly'">
          {{ $t('pricing.monthlyBilling') }}
        </button>
        <button type="button"
          :class="paymentFrequency !== 'annually' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
          class="relative ml-0.5 rounded-md border border-transparent py-2 px-6 text-sm font-medium focus:z-10 focus:outline-none"
          @click="paymentFrequency = 'annually'">
          {{ $t('pricing.annualBilling') }}
        </button>
      </div>
    </div>

    <!-- Feature comparison (xl+) -->
    <section aria-labelledby="comparison-heading" class="hidden xl:block">
      <h2 id="comparison-heading" class="sr-only">Feature comparison</h2>

      <div class="mx-auto max-w-7xl py-12 px-8">
        <div class="flex w-full items-stretch border-t border-gray-200">
          <div class="-mt-px flex w-1/5 items-end py-6 pr-4">
            <h3 class="mt-auto text-sm font-bold text-gray-900">{{ $t('pricing.featuresTitle') }}</h3>
          </div>

          <div v-for="price of pricing" :key="price.key" aria-hidden="true" class="pr-4 -mt-px pl-4 w-1/5">
            <div :class="{ 'border-transparent': true, 'border-highlight': false }"
              class="border-transparent py-6 border-t-2">
              <p :class="{ 'text-gray-900': true, 'text-highlight': false }" class="text-sm uppercase font-bold">{{
                $t(`pricing.tiers.${price.key}.name`) }}</p>
              <p class="mt-2 text-xs text-gray-500" v-html="$t(`pricing.tiers.${price.key}.description`).toString()" />
            </div>
          </div>
        </div>

        <div class="relative">
          <PricingTableBlock :keys="featuresKeys" :payment-frequency="paymentFrequency" />
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.uploadDistributionTitle') }}</h3>
        <div class="relative mt-6">
          <PricingTableBlock :keys="distributionKeys" :payment-frequency="paymentFrequency" />
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.utilitiesTitle') }}</h3>
        <div class="relative mt-6">
          <PricingTableBlock :keys="utilitiesKeys" :payment-frequency="paymentFrequency" />
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.teamsTitle') }}</h3>
        <div class="relative mt-6">
          <PricingTableBlock :keys="teamKeys" :payment-frequency="paymentFrequency" />
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.supportTitle') }}</h3>
        <div class="relative mt-6">
          <PricingTableBlock :keys="supportKeys" :payment-frequency="paymentFrequency" />
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t('pricing.3dSupportTitle') }}</h3>
        <div class="relative mt-6">
          <PricingTableBlock :keys="modellingSupport" :payment-frequency="paymentFrequency" />
        </div>
        <div class="flex">
          <p class="w-1/5" />
          <p class="text-xs text-gray-400 px-4 mt-4">{{ $t('pricing.annualOnly') }}</p>
        </div>

        <h3 class="mt-10 text-sm font-bold text-gray-900">{{ $t("pricing.billingType") }}</h3>

        <div class="relative mt-6">
          <table class="relative w-full">
            <caption class="sr-only">
              {{ $t("pricing.billingType") }}
            </caption>
            <thead>
              <tr class="text-left">
                <th scope="col">
                  <span class="sr-only">{{ $t("pricing.billingType") }}</span>
                </th>

                <th scope="col">
                  <span class="sr-only">Starter plan</span>
                </th>

                <th scope="col">
                  <span class="sr-only">Basic plan</span>
                </th>

                <th scope="col">
                  <span class="sr-only">Pro plan</span>
                </th>

                <th scope="col">
                  <span class="sr-only">Enterprise plan</span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr>
                <th scope="row" class="w-1/5 py-3 pr-4 text-left text-sm font-medium text-gray-600 relative">
                  <div class="absolute flex justify-center top-0">
                    <div class="flex rounded-lg bg-gray-100 p-0.5">
                      <button type="button"
                        :class="paymentFrequency !== 'monthly' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
                        class="relative whitespace-nowrap rounded-md py-2 px-6 text-sm font-medium shadow-sm focus:z-10 focus:outline-none"
                        @click="paymentFrequency = 'monthly'">
                        {{ $t('pricing.monthlySingle').toString() }}
                      </button>
                      <button type="button"
                        :class="paymentFrequency !== 'annually' ? 'bg-gray-100 text-standard hover:text-gray-500': 'bg-highlight text-white'"
                        class="relative ml-0.5 whitespace-nowrap rounded-md border border-transparent py-2 px-6 text-sm font-medium focus:z-10 focus:outline-none"
                        @click="paymentFrequency = 'annually'">
                        {{ $t('pricing.annualSingle').toString() }}
                      </button>
                    </div>
                  </div>
                </th>

                <td v-for="price of pricing" :key="price.key" class="px-4 relative w-1/5 py-0 text-center align-top">
                  <a v-if="!hideBuyButtons" :href="typeof price.monthlyPrice === 'number' ? panelLinks.registerUrl.value : contactUrl">
                    <button class="laptop:text-base font-medium rounded pointer-events-auto bg-highlight text-white w-full mb-8">
                      <template v-if="typeof price.monthlyPrice === 'number'">
                        {{ $t("pricing.buy") }} {{ $t(`pricing.tiers.${price.key}.name`) }}
                      </template>
                      <template v-else>
                        {{ $t("pricing.contactUs") }}
                      </template>
                    </button>
                  </a>

                  <div v-if="typeof price.monthlyPrice === 'number'">
                    <p class="text-left">
                      <span class="text-highlight">€</span><span class="text-3xl font-medium text-highlight">{{
                        $n(monthlyPayment ? price.monthlyPrice : price.annualPrice/12, {minimumFractionDigits: 2,
                        maximumFractionDigits: 2}) }}</span>
                      /{{ $t(`pricing.monthly`) }} <span v-if="!monthlyPayment">**</span>
                    </p>
                    <p v-if="!monthlyPayment" class="text-lightgray text-xs">** {{
                      $t(`pricing.payedAnnually`).toString() }} €<b>{{ $n(price.annualPrice, {minimumFractionDigits: 2,
                        maximumFractionDigits: 2}) }}</b></p>
                  </div>
                  <div v-else :class="{'text-standard': true}">
                    <p class="text-3xl">
                      <span class="text-highlight font-medium sm:mt-4">{{ $t(monthlyPayment ? price.monthlyPrice : price.annualPrice) }}</span>
                    </p>
                    <p v-if="!monthlyPayment" class="text-lightgray text-xs">&nbsp;</p>
                  </div>
                </td>
              </tr>
            </tbody>  
          </table>
        </div>
      </div>
    </section>

    <div class="faq container mx-auto flex flex-col laptop:flex-row">
      <div class="order-last laptop:order-first w-full flex laptop:ml-24 p-5 flex-col lg:flex-row md:px-8">
        <h2 class="w-full laptop:w-1/2 font-bold text-standard md:text-45xl lg:text-45xl laptop:text-55xl">
          Pricing<br>Frequently<br> Asked<br> Questions<br>FAQ</h2>
        <div class="w-full laptop:w-1/2">
          <div class="mt-8 laptop:mt-0 mb-8">
            <b>{{ $t("pricing.faq.plan_title") }}</b>
            <p v-html="$t('pricing.faq.plan_text').toString()" />
          </div>

          <div class="mb-8">
            <b>{{ $t("pricing.faq.storage_title") }}</b>
            <p v-html="$t('pricing.faq.storage_text').toString()" />
          </div>

          <div class="mb-8">
            <b>{{ $t("pricing.faq.bandwidth_title") }}</b>
            <p v-html="$t('pricing.faq.bandwidth_text').toString()" />
            <div class="mt-4 overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="text-xs uppercase tracking-wide text-standard border-gray-300">
                    <th class="w-1/4 pr-4 pb-1 text-left align-bottom border-r-2">{{ $t("pricing.faq.bandwidth_table_average_size") }}</th>
                    <th :colspan="bandwidthPlans.length" class="pl-2 pb-1 text-left align-bottom border-gray-300">
                      {{ $t("pricing.faq.bandwidth_table_average_views") }}
                    </th>
                  </tr>
                  <tr class="text-sm uppercase text-standard">
                    <th class="border-r-2"></th>
                    <th v-for="plan of bandwidthPlans" :key="plan.key" class="px-3 py-2 text-left border-b-2">{{ plan.shortname }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row of bandwidthTableRows" :key="row.modelSizeMb" class="text-standard">
                    <th scope="row" class="py-2 pr-4 text-left font-semibold border-r-2">{{ row.modelSizeMb }} MB</th>
                    <td v-for="(views, index) of row.planViews" :key="`${row.modelSizeMb}-${bandwidthPlans[index]?.key}`" class="px-3 py-2 text-standard font-semibold leading-none">
                      {{ views }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p v-html="$t('pricing.faq.bandwidth_text_2').toString()" />
          </div>

          <div class="mb-8">
            <b>{{ $t("pricing.faq.seats_title") }}</b>
            <p v-html="$t('pricing.faq.seats_text').toString()" />
          </div>

          <div class="mb-8">
            <b>{{ $t("pricing.faq.planswitch_title") }}</b>
            <p v-html="$t('pricing.faq.planswitch_text').toString()" />
          </div>

          <div class="mb-8">
            <b>{{ $t("pricing.faq.early_access_title") }}</b>
            <p v-html="$t('pricing.faq.early_access_text').toString()" />
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Tooltip from '@/components/Tooltip.vue'

import imgBasic from '~/assets/img/pricing/starter.svg'
import imgProfessional from '~/assets/img/pricing/basic.svg'
import imgBusiness from '~/assets/img/pricing/professional.svg'
import imgEnterprise from '~/assets/img/pricing/enterprise.svg'
import { usePanelLinks } from '~/composables/PanelLinks'
import PricingTableBlock from './PricingTableBlock.vue'

// the following must match the keys in the pricingData array
// and the keys in the i18n files
const featuresKeys = [
    'editor',
    'webViewer',
    'arViewer',
    'productSelector',
    'hotspots',
    'customLogos',
    'analytics',
    'artag',
    'objectCapture',
]

const distributionKeys = [
  'uploads',
  'storage',
  'bandwidth',
  'shareLink',
  'embed',
  'qrCode',
  'unlistedModels'
]
const supportKeys = [
  'onlineDocs',
  'videoTutorials',
  'support',
  'accountManager',
]
const modellingSupport = [
  'modellingSupport',
]
const teamKeys = [
  'seatsCreator',
  'seatsViewer',
  'permissionManagement',
  'folderAccess',
]
const utilitiesKeys = [
  'downloadAssets',
  'duplicateConfiguration'
]

export interface PricingComplexEntry { 
  value: string, 
  paymentFrequency?: string,
}

export const pricingData: {[key:string]: string|number|boolean|PricingComplexEntry}[] = [
  {
    key: 'basic',
    shortname: 'Basic',
    icon: imgBasic,
    editor: true,
    webViewer: true,
    arViewer: true,
    productSelector: true,
    hotspots: true,
    storage: 0.5,
    bandwidth: 5,
    shareLink: true,
    embed: true,
    qrCode: true,
    unlistedModels: true,
    uploads: 'pricing.uploadsLimited',
    modelViews: 1024,
    monthlyPrice: 9.99,
    annualPrice: 109.99,
    onlineDocs: true,
    videoTutorials: true,
    support: false,
    accountManager: false,
    modellingSupport: false,
    customLogos: false,
    seatsCreator: 1,
    seatsViewer: 0,
    permissionManagement: false,
    folderAccess: false,
    analytics: false,
    downloadAssets: true,
    duplicateConfiguration: true,
    artag: 'pricing.earlyAccess',
    objectCapture: 'pricing.earlyAccess',
  },
  {
    key: 'professional',
    shortname: 'Pro',
    icon: imgProfessional,
    editor: true,
    webViewer: true,
    arViewer: true,
    productSelector: true,
    hotspots: true,
    storage: 5,
    bandwidth: 50,
    shareLink: true,
    embed: true,
    qrCode: true,
    unlistedModels: true,
    uploads: 'pricing.uploadsUnlimited',
    modelViews: 10240,
    monthlyPrice: 79.99,
    annualPrice: 799.99,
    onlineDocs: true,
    videoTutorials: true,
    support: 'pricing.hours.48',
    accountManager: false,
    modellingSupport: false,
    customLogos: true,
    seatsCreator: 1,
    seatsViewer: 0,
    permissionManagement: false,
    folderAccess: false,
    analytics: true,
    downloadAssets: true,
    duplicateConfiguration: true,
    artag: 'pricing.earlyAccess',
    objectCapture: 'pricing.earlyAccess',
  },
  {
    key: 'business',
    shortname: 'Business',
    icon: imgBusiness,
    editor: true,
    webViewer: true,
    arViewer: true,
    productSelector: true,
    hotspots: true,
    storage: 15,
    bandwidth: 150,
    shareLink: true,
    embed: true,
    qrCode: true,
    unlistedModels: true,
    uploads: 'pricing.uploadsUnlimited',
    modelViews: 51200,
    // monthlyPrice: 169.99,
    // annualPrice: 1699.00,
    // TODO: temporary coming soon for business
    monthlyPrice: 'pricing.comingSoon',
    annualPrice: 'pricing.comingSoon',
    onlineDocs: true,
    videoTutorials: true,
    support: 'pricing.hours.24',
    accountManager: false,
    modellingSupport: { value: 'pricing.hours.4', paymentFrequency: 'yearly'},
    customLogos: true,
    seatsCreator: 2,
    seatsViewer: 2,
    permissionManagement: true,
    folderAccess: true,
    analytics: true,
    downloadAssets: true,
    duplicateConfiguration: true,
    artag: 'pricing.earlyAccess',
    objectCapture: 'pricing.earlyAccess',
  },
  {
    key: 'enterprise',
    shortname: 'Enterprise',
    icon: imgEnterprise,
    editor: true,
    webViewer: true,
    arViewer: true,
    productSelector: true,
    hotspots: true,
    storage: 'pricing.custom',
    bandwidth: 'pricing.custom',
    shareLink: true,
    embed: true,
    qrCode: true,
    unlistedModels: true,
    uploads: 'pricing.uploadsUnlimited',
    modelViews: 'pricing.custom',
    monthlyPrice: 'pricing.custom',
    annualPrice: 'pricing.custom',
    onlineDocs: true,
    videoTutorials: true,
    support: 'pricing.custom',
    accountManager: true,
    modellingSupport: { value: 'pricing.custom', paymentFrequency: 'yearly'},
    customLogos: true,
    seatsCreator: 'pricing.custom',
    seatsViewer: 'pricing.custom',
    permissionManagement: true,
    folderAccess: true,
    analytics: true,
    downloadAssets: true,
    duplicateConfiguration: true,
    artag: 'pricing.earlyAccess',
    objectCapture: 'pricing.earlyAccess',
  },
]
</script>
<script setup lang="ts">
  const panelLinks = usePanelLinks()
  const { t } = useI18n()
  const localePath = useLocalePath()

  const contactUrl = computed( () => localePath(t('menuFooter.contacts.path').toString()) )

  // Props
  const props = withDefaults(defineProps<{
    hideBuyButtons?: boolean
  }>(), {
    hideBuyButtons: false
  })

  const paymentFrequency = ref('annually')
  // const maxDiscountPercentage = ref('11%')
  const selectedPlan = ref('professional')
  const pricing = pricingData

  const filteredPlans = computed(() => {
    return pricing.filter(plan => plan.key === selectedPlan.value)
  })

  const bandwidthModelSizesMb = [2.5, 5, 10]
  const mbPerGb = 1000

  const bandwidthPlans = computed(() => {
    const planOrder = ['basic', 'professional', 'business']

    return planOrder
      .map(planKey => pricing.find(plan => plan.key === planKey))
      .filter((plan): plan is { [key: string]: string|number|boolean|PricingComplexEntry } => plan !== undefined)
      .filter(plan => typeof plan.bandwidth === 'number')
      .map(plan => ({
        key: String(plan.key),
        shortname: String(plan.shortname).toUpperCase(),
        bandwidthGb: Number(plan.bandwidth),
      }))
  })

  const bandwidthTableRows = computed(() => {
    return bandwidthModelSizesMb.map(modelSizeMb => ({
      modelSizeMb,
      planViews: bandwidthPlans.value.map(plan => {
        const monthlyViews = Math.floor((plan.bandwidthGb * mbPerGb) / modelSizeMb)
        return new Intl.NumberFormat('en-US').format(monthlyViews)
      }),
    }))
  })

  const monthlyPayment = computed(() => {
    return paymentFrequency.value === 'monthly'
  })
</script>

<style lang="css" scoped>
input[type=radio] {
  width: 1.625rem;
  min-width: 1.625rem;
  height: 1.625rem;
  border-radius: 100%;
  border: 1px solid white;
  appearance: none;
}

input[type=radio]:checked {
  background: radial-gradient(var(--color-highlight) 45%, rgba(255, 0, 0, 0) 51%);
}

.faq p, .faq b {
  @apply laptop:text-slg md:text-base leading-7
}

.faq >>> a {
  color: var(--color-highlight);
}
</style>
